import MainLayout from "../../components/layouts/MainLayout"
import React, { useEffect, useState } from "react";
import { EyeFilled, SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import { Table, Input, Select, DatePicker, Button, Tag, Space } from "antd";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Auth } from "../../utils/Auth";
import { DateFormat } from "../../utils/formatDate";
import { FactureApi } from "../../api/factureApi";
import Money from "../../components/Money";

const { RangePicker } = DatePicker;

const statutColors = {
    'EN_VALIDATION_PAIEMENT': 'orange',
    'PAYEE': 'green',
    'ANNULEE': 'red',
}

const ListFacturesView = () => {
    const navigate = useNavigate()
    const abortController = new AbortController()

    const [factures, setFactures] = useState([])
    const [loading, setLoading] = useState(false)
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(15)

    const [numeroFacture, setNumeroFacture] = useState('')
    const [orderNumber, setOrderNumber] = useState('')
    const [statut, setStatut] = useState(undefined)
    const [dateRange, setDateRange] = useState(null)

    const buildFilters = (currentPage, currentPerPage) => {
        const filters = {
            page: currentPage,
            per_page: currentPerPage,
        }

        if (numeroFacture) filters.numero_facture = numeroFacture
        if (orderNumber) filters.order_number = orderNumber
        if (statut) filters.statut = statut
        if (dateRange && dateRange.length === 2) {
            filters.date_debut = dateRange[0].format('YYYY-MM-DD')
            filters.date_fin = dateRange[1].format('YYYY-MM-DD')
        }

        return filters
    }

    const hasActiveSearch = (filters) => {
        return Boolean(filters.numero_facture || filters.order_number || filters.statut || filters.date_debut || filters.date_fin)
    }

    const handleFetchData = async (currentPage = page, currentPerPage = perPage) => {
        setLoading(true)
        try {
            const filters = buildFilters(currentPage, currentPerPage)

            const res = hasActiveSearch(filters)
                ? await FactureApi.searchFactures(filters, abortController.signal)
                : await FactureApi.getFactures(filters, abortController.signal)

            if (!res || !res.success) {
                setLoading(false)
                return
            }

            setFactures(res.data.data ?? [])
            setTotal(res.data.total ?? 0)
            setPage(res.data.current_page ?? currentPage)
            setPerPage(res.data.per_page ?? currentPerPage)
            setLoading(false)
        } catch (error) {
            console.log(error)
            setLoading(false)
            const messages = await error?.messages ?? error?.message

            if (messages instanceof Array)
                return Swal.fire({
                    title: messages.join('\n') !== 'Unauthenticated' ? "Erreur détectée" : "Session expirée",
                    text: messages.join('\n') !== 'Unauthenticated' ? "Veuillez vous reconncter." : "Votre session a expirée veuillez vous reconnecter",
                    icon: "error",
                }).then(() => {
                    if (messages.join('\n') === 'Unauthenticated') {
                        Auth.removeSessionToken()
                        return navigate(`/login`)
                    }
                });
        }
    }

    const handleSearch = () => {
        handleFetchData(1, perPage)
    }

    const handleReset = () => {
        setNumeroFacture('')
        setOrderNumber('')
        setStatut(undefined)
        setDateRange(null)
        setTimeout(() => handleFetchData(1, perPage), 0)
    }

    const handleDetail = (facture) => {
        if (facture.id)
            navigate(`/factures/${facture.id}/details`)
    }

    const handleTableChange = (pagination) => {
        handleFetchData(pagination.current, pagination.pageSize)
    }

    useEffect(() => {
        handleFetchData(1, perPage)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const columns = [
        {
            title: 'N° Facture',
            dataIndex: 'numero_facture',
            key: 'numero_facture',
        },
        {
            title: 'N° Commande',
            dataIndex: 'order_number',
            key: 'order_number',
            render: (value) => value || 'Non renseigné',
        },
        {
            title: 'Vendeur',
            key: 'vendeur',
            render: (_, facture) => facture?.vendeur ? `${facture.vendeur.name ?? ''} ${facture.vendeur.lastname ?? ''}` : 'Non renseigné',
        },
        {
            title: 'Client',
            key: 'client',
            render: (_, facture) => facture?.client ? `${facture.client.name ?? ''} ${facture.client.lastname ?? ''}` : 'Non renseigné',
        },
        {
            title: 'Montant TTC',
            dataIndex: 'montant_total_ttc',
            key: 'montant_total_ttc',
            render: (value) => <Money amount={value} />,
        },
        {
            title: 'Date de dépôt',
            dataIndex: 'date_emission',
            key: 'date_emission',
            render: (value) => DateFormat.formatDate(value),
        },
        {
            title: 'Statut',
            dataIndex: 'statut',
            key: 'statut',
            render: (value) => value ? <Tag color={statutColors[value] || 'default'}>{value}</Tag> : '-',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, facture) => (
                <EyeFilled
                    className="text-green-600 cursor-pointer hover:text-green-800"
                    onClick={() => handleDetail(facture)}
                />
            ),
        },
    ]

    return <MainLayout>
        <div className="container mx-auto p-4">
            <h1 className="text-xl font-bold mb-4">Gestion des factures</h1>

            <div className="bg-white p-4 rounded shadow mb-4">
                <Space wrap>
                    <Input
                        placeholder="N° facture"
                        value={numeroFacture}
                        onChange={(e) => setNumeroFacture(e.target.value)}
                        style={{ width: 180 }}
                    />
                    <Input
                        placeholder="N° commande"
                        value={orderNumber}
                        onChange={(e) => setOrderNumber(e.target.value)}
                        style={{ width: 180 }}
                    />
                    <Select
                        placeholder="Statut"
                        value={statut}
                        onChange={setStatut}
                        allowClear
                        style={{ width: 220 }}
                        options={[
                            { value: 'EN_VALIDATION_PAIEMENT', label: 'En validation de paiement' },
                            { value: 'PAYEE', label: 'Payée' },
                            { value: 'ANNULEE', label: 'Annulée' },
                        ]}
                    />
                    <RangePicker value={dateRange} onChange={setDateRange} />
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                        Rechercher
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={handleReset}>
                        Réinitialiser
                    </Button>
                </Space>
            </div>

            <Table
                dataSource={factures}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{
                    current: page,
                    pageSize: perPage,
                    total: total,
                    showSizeChanger: true,
                }}
                onChange={handleTableChange}
            />
        </div>
    </MainLayout>
}

export default ListFacturesView
